import {
  CalendarNav,
  Datepicker,
  Eventcalendar,
  formatDate,
  momentTimezone,
  setOptions,
  toast,
} from "@mobiscroll/react";
import {
  OwcBadge,
  OwcIcon,
  OwcIconButton,
  OwcListItem,
  OwcSelectDropdown,
  OwcTypography,
  OwcButton,
  OwcHyperlink,
  OwcTooltip,
} from "@one/react";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import "./CalendarTimeline.scss";
import {
  BOOKING_TYPE,
  IconStatus,
  RESOURCE,
  singleEventSkeleton,
  TIMELINE_TYPE,
  TIMELINE_CONFIG,
  TIMELINE_BUFFER_UNITS,
} from "../../../constants";
import moment from "moment-timezone";
import {
  changeDateToUtcString,
  isSameDayBooking,
} from "../../../utils/helpers/text";
import BookingScheduleStatus from "./BookingScheduleStatus";
import { cloneDeep, sortBy, uniqueId } from "lodash";
import { BookingContext, BookingPopUpContext } from "../../booking/context";
import { getAllData } from "../../../utils/helpers/fetching";
import { withApollo } from "react-apollo";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import {
  FIND_BOOKABLE_INSTRUMENTS,
  GET_DIGITAL_LAB_BOOKING_INSTRUMENTS,
  GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
} from "../../../gql/bookingapi";
import { useGoogleCalendar } from "./hooks/useGoogleCalendar";
import {
  BOOKING_STORAGE_KEYS,
  getPersistedFilter,
  setPersistedFilter,
} from "../../../utils/store/bookingFilterPersistence";
import { eachDayOfInterval } from "date-fns";
import { CustomFilterContext } from "../../booking-instruments/booking-instruments-custom-filter/context";
import {
  getBookingCategory,
  getBookingDisplayText,
} from "../../../components/shared/helpFunctions";
import { formatTitle } from "../../../utils/helpers/googleCalendar";
import { handleOnCellClick } from "../../booking-calendars/helpers";
import TimelineBookingEvent from "./TimelineBookingEvent.tsx";
import RecurringBookingContext from "./RecurringBooking/Context/RecurringBookingContext";
import { RecurringBookingPicker } from "./RecurringBooking/RecurringBookingPicker";
import ConflictPanel from "./RecurringBooking/ConflictPanel";
import {
  buildStandardPageEvents,
  getLatestEventDetailsStartDate,
  getTimelineBoundsFromPageEvent,
  mergeTimelineReservations,
  shouldLoadLatestEventDetails,
  shouldSkipStandardPageLoading,
  shouldSyncDatePickerFromPendingReservations,
} from "./pageLoading.utils";
// setup Mobiscroll Moment plugin
momentTimezone.moment = moment;

const GROUP_BY_OPTIONS = [
  { id: "cluster", label: "Cluster ID" },
  { id: "category", label: "Category" },
  { id: "model", label: "Model" },
  { id: "location", label: "Location" },
  { id: "equipment", label: "Equipment" }, // flat
];

setOptions({
  theme: "material",
  themeVariant: "light",
});

const VIEW_COLUMNS = {
  [TIMELINE_TYPE.DAY]: 12, // 12 columns for 12 hours visible (8am-8pm typically)
  [TIMELINE_TYPE["3DAY"]]: 12, // 12 columns for 6-hour intervals across 3 days
  [TIMELINE_TYPE.WEEK]: 7, // 7 columns for 7 days
  [TIMELINE_TYPE.MONTH]: 4, // 4 columns for 4 weeks in month view
};

// Minimal width when datepicker with recurring is comfortably fitting into viewport
const datepickerViewportWidthLimit = 1650;

export const alignDatePicker = (viewportWidth, isEditMode = false, isDuplicate = false) => {
  if (isEditMode && !isDuplicate) {
    return 'start';
  }

  if (viewportWidth > datepickerViewportWidthLimit) {
    return 'start';
  }

  return 'end';
};

/**
 * Generate a consistent color for a user based on their email/name
 *
 * @param {string} identifier - User email or name
 * @returns {string} - Hex color code
 */
const generateUserColor = (identifier) => {
  if (!identifier) return "#8B9BB3";

  // Predefined darker pastel color palette for avatars (better contrast with white text)
  const colors = [
    "#E89CAE",
    "#E6C18B",
    "#E6E68B",
    "#9FE69F",
    "#8BC5E6",
    "#A8B5D1",
    "#E68BB5",
    "#B695E6",
    "#8BB5E6",
    "#E6C78B",
    "#B5E68B",
    "#8BE6C7",
    "#E6A18B",
    "#C78BE6",
    "#8BA1E6",
    "#E6B58B",
    "#A1C7A1",
    "#E6C195",
    "#C7A1C7",
    "#95D185",
    "#D1A1A8",
    "#A1C1E6",
    "#D1B385",
    "#B5A1D1",
    "#9FD19F",
  ];

  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const CustomEventCalendar = ({
  eventData,
  resourceData,
  visibleDatePicker = true,
  selectedStartDate,
  setSelectedStartDate,
  selectedEndDate,
  setSelectedEndDate,
  dataTimeZone,
  displayTimeZone,
  clearPendingReservation,
  updatePendingReservation,
  removeEquipmentAndReservation,
  pendingReservations,
  getReservationBooking,
  getEventDetails,
  setloading: setLoading,
  setEventsData,
  loading,
  view,
  setView,
  calView,
  setCalView,
  manipulatePopoverContent,
  siteTimezone,
  newEquipmentIds,
  setNewEquipmentIds,
  getLatestEventDetailsOnly,
  setCancelStatus,
  setDeleteResourceId,
  client,
  isTimelineView,
  user = null,
  renderCustomEquipmentEvent = null,
  originalSelectedStartDate = null,
  originalSelectedEndDate = null,
  onPermissionCheckersReady = null,
  isGroupView = false,
  handleCreateBookingFromEquipmentView = null,
  bookingFetchError = false,
}) => {
  const { isEditMode, isDuplicate, editBookingData, isOngoing } =
    useContext(BookingContext);
  const { dispatchAction: bookingPopUpDispatchAction } =
    useContext(BookingPopUpContext);
  const { selectedList } = useContext(CustomFilterContext);
  const calendarRef = useRef(null);
  const [timelineSelectedDate, setTimelineSelectedDate] = useState(null);
  const [eventHover, setEventHover] = useState(null);
  const [rangeInst, setRangeInst] = useState();
  const [colorsData, setColorsData] = useState([]);
  const [labelsData, setLabelsData] = useState([]);
  const [disableDateList, setDisableDateList] = useState([]);
  const [calendarInstance, setCalendarInstance] = useState(null);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [equipmentPermissions, setEquipmentPermissions] = useState(new Map());
  const [equipmentBookingCapabilities, setEquipmentBookingCapabilities] =
    useState(new Map());
  const usersList = useSelector((state) => state.users.users);
  const {
    isRecurringBooking,
    temporaryStartDate,
    temporaryEndDate,
    temporarySelectedDates,
    setTemporaryStartDate,
    setTemporaryEndDate,
    debouncedSetTempDates,
    conflicts,
    setEventDataContext,
    setRecoursesContext,
    getTrimmedRRuleString,
    resetAll
  } = useContext(RecurringBookingContext);
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);
  const [isEquipmentHdrOpen, setEquipmentHdrOpen] = useState(false);
  const [localFetchError, setLocalFetchError] = useState(false);
  const hasServerError = bookingFetchError || localFetchError;
  const [groupBy, setGroupBy] = useState(() => {
    return getPersistedFilter(
      BOOKING_STORAGE_KEYS.TIMELINE_GROUP_BY_VIEW,
      "category",
    );
  });
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const isDatepickerOpen = rangeInst?._isOpen || false;
  const isDatepickerCancelledRef = useRef(false);
  const dpCloseReasonRef = useRef(null);
  const dateChangeInProgressRef = useRef(false);
  const slotUpdateInProgressRef = useRef(false);

  useEffect(() => {
    const checkViewport = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  useEffect(() => {
    setTemporaryStartDate(originalSelectedStartDate);
    setTemporaryEndDate(originalSelectedEndDate);
  }, [originalSelectedEndDate, originalSelectedStartDate, setTemporaryStartDate, setTemporaryEndDate])

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (isTvMode) root.classList.add("no-scroll");
    else root.classList.remove("no-scroll");
    return () => root.classList.remove("no-scroll");
  }, [isTvMode]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        setPersistedFilter(
          BOOKING_STORAGE_KEYS.TIMELINE_GROUP_BY_VIEW,
          groupBy,
        );
      } catch (err) {
        console.warn("Failed to persist TIMELINE_GROUP_BY_VIEW:", err);
      }
    }, 100);
    return () => clearTimeout(timeoutId); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const equipmentMetaById = useMemo(() => {
    const idMap = new Map();
    (eventData || []).forEach((event) => {
      const eq = event.equipment;
      const id = eq?.inventoryId || eq?.addInfo?.inventoryId;
      if (id && !idMap.has(id)) idMap.set(id, eq);
    });

    (resourceData || []).forEach((eq) => {
      const id = eq?.inventoryId || eq?.id;

        // Skip if there's no valid ID
        if (!id) return;

        // In timeline view, do not overwrite existing entries
        if (isTimelineView && idMap.has(id)) return;

        const normalizedEq = {
          inventoryId: id,
          shortIdentifier: eq.shortIdentifier || eq.name || eq.equipmentModel || id,
          equipmentModel: eq.equipmentModel || eq.modelName,
          equipmentId: eq.equipmentId || "",
          location: eq.location || eq.siteName,
          siteName: eq.siteName,
          cluster: eq.cluster,
          equipmentCategory: eq.equipmentCategory,
          ...eq,
        };

        idMap.set(id, normalizedEq);
    });

    return idMap;
  }, [eventData, resourceData, isTimelineView]);

  const leafResources = useMemo(() => {
    return Array.from(equipmentMetaById.values()).map((eq) => ({
      id: eq.inventoryId,
      name:
        eq.shortIdentifier ||
        `${eq.equipmentModel}${eq.equipmentId ? " - " + eq.equipmentId : ""}`,
      color: "#4981d6",
      eventCreation: true,
    }));
  }, [equipmentMetaById]);

  const getGroupKey = (eq, groupById) => {
    const groupByValue = (groupById || "EQUIPMENT").toUpperCase();
    switch (groupByValue) {
      case "CLUSTER": // we group by Cluster attribute (not by Cluster Id which is for cluster only bookable as a whole)
        return eq.cluster || "Empty";
      case "MODEL":
        return eq.equipmentModel || "Empty";
      case "CATEGORY":
        return eq.equipmentCategory || "Empty";
      case "LOCATION":
        return eq.location || eq.siteName || "Empty";
      case "EQUIPMENT":
      default:
        return null;
    }
  };

  const groupedResources = useMemo(() => {
    const groupByValue = (groupBy || "EQUIPMENT").toUpperCase();

    if (groupByValue === "EQUIPMENT") {
      return [...leafResources].sort((a, b) => a.name.localeCompare(b.name));
    }

    const buckets = new Map();

    leafResources.forEach((leaf) => {
      const meta = equipmentMetaById.get(leaf.id);
      const key = (meta && getGroupKey(meta, groupByValue)) || "Empty";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(leaf);
    });

    let idx = 0;
    return Array.from(buckets.entries())
      .sort(([aName], [bName]) => aName.localeCompare(bName))
      .map(([groupName, items]) => {
        return {
          id: `grp_${groupByValue.toLowerCase()}_${idx++}`,
          name: groupName,
          eventCreation: false,
          collapsed: false,
          children: [...items].sort((a, b) => a.name.localeCompare(b.name)),
        };
      });
  }, [leafResources, equipmentMetaById, groupBy]);

  // Google Calendar integration
  const {
    isLoggedIn: isGoogleLoggedIn,
    selectedCalendarIds,
    googleEvents,
    loadCalendarEvents,
    calendarData,
    toggleCalendar,
  } = useGoogleCalendar();
  const [googleCalendarResources, setGoogleCalendarResources] = useState([]);

  /**
   * Check if user has permission to book a specific equipment Uses the same
   * logic as EquipmentMainPage checkbox function:
   *
   * - For booking controlled equipment: checks user.id and user.user in
   *   bookableForUsers array
   * - If controlled and user not in list: allows BookingAdmin role access
   * - For non-controlled equipment: allows all users
   *
   * @param {string} inventoryId - Equipment inventory ID
   * @returns {object} - Permission status and reason if denied
   */
  const checkEquipmentPermission = useCallback(
    async (inventoryId) => {
      try {
        // First check equipment booking capabilities
        const equipmentDetails = await client.query({
          query: GET_DIGITAL_LAB_BOOKING_INSTRUMENTS,
          variables: { inventoryId },
          fetchPolicy: "no-cache",
        });
        const equipment = equipmentDetails.data?.getDigitalLabBookingInstrument;

        if (!equipment) {
          return { canBook: false, reason: "Equipment not found" };
        }

        if (!equipment.isBookable) {
          return { canBook: false, reason: "Equipment is not bookable" };
        }

        // Apply the EXACT same logic as EquipmentMainPage checkbox function
        // In GraphQL response, isBookingControlled is boolean, in CloudSearch it's array
        const isBookingControlled =
          equipment.addInfo?.isBookingControlled === true;

        let bookableForUsers = true;
        if (isBookingControlled) {
          // Check if user is in the bookable users list using same logic as EquipmentMainPage
          const bookableUsersList = equipment.addInfo?.bookableForUsers;
          if (bookableUsersList !== undefined && bookableUsersList) {
            bookableForUsers = bookableUsersList.some(
              (bookableUser) =>
                bookableUser === user?.id || bookableUser === user?.user,
            );
          } else {
            bookableForUsers = false;
          }
        }

        // Apply the EXACT same logic as EquipmentMainPage: if controlled and user not in list, check admin
        if (isBookingControlled && !bookableForUsers) {
          const isBookingAdmin = user?.roles?.some(
            (role) => role === "BookingAdmin",
          );
          return {
            canBook: !!isBookingAdmin,
            reason: isBookingAdmin
              ? null
              : "User not in authorized users list - Admin access only",
          };
        }

        // For non-controlled equipment or users with access, always allow
        return { canBook: true, reason: null };
      } catch (error) {
        console.error("Error checking equipment permission:", error);
        return { canBook: false, reason: "Error checking permissions" };
      }
    },
    [client, user],
  );

  /**
   * Check equipment booking slots availability
   *
   * @param {string} inventoryId - Equipment inventory ID
   * @param {string} startDate - Start date for checking
   * @param {string} endDate - End date for checking
   * @returns {object} - Availability status and existing bookings
   */
  const checkEquipmentAvailability = useCallback(
    async (inventoryId, startDate, endDate) => {
      try {
        const bookingEntries = await client.query({
          query: GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
          variables: {
            site: user?.site,
            inventoryId: { eq: inventoryId },
            filter: {
              start: { between: [startDate, endDate] },
            },
            limit: 1000,
          },
          fetchPolicy: "no-cache",
        });

        if (bookingEntries?.errors && bookingEntries.errors.length > 0) {
          setLocalFetchError(true);
          snackbarService.show({
            message: "Unexpected Server Error",
            type: "error",
          });
          return {
            hasConflicts: false,
            existingBookings: [],
            availableSlots: [],
            hasError: true,
          };
        }

        const entries =
          bookingEntries.data?.bookingEntriesBySiteAndInventoryId?.items || [];
        const activeBookings = entries.filter((entry) => entry.active);

        return {
          hasConflicts: activeBookings.length > 0,
          existingBookings: activeBookings,
          availableSlots: entries.filter(
            (entry) => entry.type === BOOKING_TYPE.AVAILABLE,
          ),
          hasError: false,
        };
      } catch (error) {
        console.error("Error checking equipment availability:", error);
        setLocalFetchError(true);
        snackbarService.show({
          message: "Unexpected Server Error",
          type: "error",
        });
        return {
          hasConflicts: false,
          existingBookings: [],
          availableSlots: [],
          hasError: true,
        };
      }
    },
    [client, user],
  );

  /** Batch check permissions and capabilities for all equipment */
  const checkAllEquipmentPermissions = useCallback(async () => {
    if (!leafResources.length || !user) return;

    const permissionsMap = new Map();
    const capabilitiesMap = new Map();

    await Promise.all(
      leafResources.map(async (res) => {
        const inventoryId = res.id;
        if (!inventoryId) return;

        try {
          const permission = await checkEquipmentPermission(inventoryId);
          permissionsMap.set(inventoryId, permission);
          capabilitiesMap.set(inventoryId, {
            canCreate: permission.canBook,
            canView: true, // Generally all equipment can be viewed
            reason: permission.reason,
          });
        } catch (error) {
          permissionsMap.set(inventoryId, {
            canBook: false,
            reason: "Permission check failed",
          });
          capabilitiesMap.set(inventoryId, {
            canCreate: false,
            canView: true,
            reason: "Permission check failed",
          });
        }
      }),
    );

    setEquipmentPermissions(permissionsMap);
    setEquipmentBookingCapabilities(capabilitiesMap);
  }, [leafResources, user, checkEquipmentPermission]);

  /**
   * Get permission status for a specific equipment
   *
   * @param {string} inventoryId - Equipment inventory ID
   * @returns {object} - Permission and capability status
   */
  const getEquipmentBookingStatus = useCallback(
    (inventoryId) => {
      const permissions = equipmentPermissions.get(inventoryId);
      const capabilities = equipmentBookingCapabilities.get(inventoryId);

      return {
        canBook: permissions?.canBook && capabilities?.canCreate,
        reason: permissions?.reason || capabilities?.reason,
        permissions,
        capabilities,
      };
    },
    [equipmentPermissions, equipmentBookingCapabilities],
  );

  /**
   * Check if user has at least one equipment they can book
   *
   * @returns {boolean} - True if user can book at least one equipment
   */
  const hasBookableEquipment = useCallback(() => {
    if (!leafResources.length) return false;
    return leafResources.some((resource) => {
      const status = getEquipmentBookingStatus(resource.id);
      return !!status.canBook;
    });
  }, [leafResources, getEquipmentBookingStatus]);

  /** Store the calendar instance when initialized */
  const handleCalendarInit = useCallback(
    (event) => {
      setCalendarInstance(event.inst);
    },
    [setCalendarInstance],
  );

  const renderGroupByDropdown = () => {
    return (
      <div className="owc-groupby-wrap">
        <OwcTypography variant="body2" className="owc-groupby-title">
          Group equipment by:
        </OwcTypography>

        <OwcSelectDropdown
          className="owc-groupby-dd"
          style={{ minWidth: 220 }}
          size="compact"
          outlined
          appendTo="body"
          value={[groupBy]}
          onSelectChange={(ev) => {
            const arr = Array.isArray(ev.detail) ? ev.detail : [];
            const raw = arr[0];

            const nextId =
              typeof raw === "number"
                ? GROUP_BY_OPTIONS[raw]
                  ? GROUP_BY_OPTIONS[raw].id
                  : groupBy
                : raw;

            if (nextId && nextId !== groupBy) {
              setGroupBy(nextId);
              setEquipmentHdrOpen((v) => !v);
            }
          }}
        >
          {GROUP_BY_OPTIONS.map((group) => (
            <OwcListItem
              key={group.id}
              value={group.id}
              selected={groupBy === group.id}
            >
              {group.label}
            </OwcListItem>
          ))}
        </OwcSelectDropdown>
      </div>
    );
  };

  useEffect(() => {
    // In edit mode, a dedicated effect below controls timelineSelectedDate.
    // Ignore originalSelectedStartDate/EndDate updates from updateDatePicker:
    // React 17 applies them in separate renders, which can shift the timeline.
    if (isEditMode) return;

    if (isTimelineView && !timelineSelectedDate) {
      setTimelineSelectedDate(moment().tz(siteTimezone).toDate());
    } else if (
      originalSelectedStartDate &&
      originalSelectedEndDate &&
      !pendingReservations.length
    ) {
      // In new booking mode, use original selected dates if they were actually selected by user
      // BUT only if no pending reservations exist (to prevent jumping during block selection)
      setTimelineSelectedDate(originalSelectedStartDate);
    } else if (!timelineSelectedDate) {
      const todayDate = moment().tz(siteTimezone).toDate();
      setTimelineSelectedDate(todayDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    originalSelectedStartDate,
    originalSelectedEndDate,
    editBookingData?.dateFrom,
  ]);

  // In edit mode, initialize/re-initialize the timeline position only when the
  // booking itself changes (editBookingData?.dateFrom). Slot create/update/delete
  // never touch editBookingData, so this effect never fires during slot operations.
  // handleDateChange explicitly sets timelineSelectedDate when the user picks new dates.
  useEffect(() => {
    if (isEditMode && editBookingData?.dateFrom) {
      setTimelineSelectedDate(
        moment(editBookingData.dateFrom).tz(siteTimezone).toDate(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editBookingData?.dateFrom]);

  // For equipment view, always show current time initially (only on mount)
  useEffect(() => {
    if (isTimelineView && !timelineSelectedDate) {
      const todayDate = moment().tz(siteTimezone).toDate();
      setTimelineSelectedDate(todayDate);
    }
    // eslint-disable-next-line
  }, [isTimelineView, siteTimezone]);

  // Initialize equipment view configuration
  useEffect(() => {
    if (isTimelineView && !calView?.timeline) {
      const equipmentCalView = {
        timeline: {
          type: "week",
          allDay: false,
          resolutionHorizontal: "hour",
          currentTimeIndicator: true,
        },
      };
      setCalView(equipmentCalView);
      setView("week");
    }
  }, [isTimelineView, setCalView, setView, calView]);

  useEffect(() => {
    if (
      isTimelineView &&
      resourceData &&
      resourceData.length > 0 &&
      user &&
      client
    ) {
      checkAllEquipmentPermissions();
    }
  }, [
    isTimelineView,
    resourceData,
    user,
    client,
    checkAllEquipmentPermissions,
  ]);

  useEffect(() => {
    if (onPermissionCheckersReady && isTimelineView) {
      onPermissionCheckersReady({
        checkEquipmentPermission,
        checkEquipmentAvailability,
        getEquipmentBookingStatus,
        hasBookableEquipment,
        equipmentPermissions,
        equipmentBookingCapabilities,
      });
    }
  }, [
    onPermissionCheckersReady,
    isTimelineView,
    checkEquipmentPermission,
    checkEquipmentAvailability,
    getEquipmentBookingStatus,
    hasBookableEquipment,
    equipmentPermissions,
    equipmentBookingCapabilities,
  ]);

  /**
   * Change the view of timeline
   *
   * @param {any} view - Selected view
   * @returns - SetView & setCalView
   */
  const changeView = useCallback(
    (view) => {
      let calView;

      switch (view) {
        case TIMELINE_TYPE["3DAY"]:
          calView = { ...TIMELINE_CONFIG["3DAY"] };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
        case TIMELINE_TYPE.WEEK:
          calView = { ...TIMELINE_CONFIG.WEEK };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
        case TIMELINE_TYPE.MONTH:
          calView = { ...TIMELINE_CONFIG.MONTH };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
        default:
          calView = { ...TIMELINE_CONFIG.DAY };
          calView.timeline = {
            ...calView.timeline,
          };
          break;
      }
      if (isTvMode) {
        calView.timeline = {
          ...calView.timeline,
          type: "day",
          allDay: false,
          startTime: "06:00",
          endTime: "20:00",
          timeCellStep: 60,
          timeLabelStep: 60,
          currentTimeIndicator: true,
        };
      }
      setCalView(calView);
      setView(view); // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setCalView, isTvMode, setView],
  );

  // Watch for view changes and call changeView only once
  useEffect(() => {
    if (view) {
      changeView(view);
    }
  }, [view, changeView]);

  useEffect(() => {
    if (isTvMode) {
      if (view !== TIMELINE_TYPE.DAY) setView(TIMELINE_TYPE.DAY);

      setTimeout(
        () =>
          setCalView({
            ...TIMELINE_CONFIG.DAY,
            size: 1,
            timeline: {
              ...TIMELINE_CONFIG.DAY?.timeline,
              type: "day",
              allDay: false,
              startTime: "06:00",
              endTime: "20:00",
              timeCellStep: 60,
              timeLabelStep: 60,
              currentTimeIndicator: true,
              size: 1,
            },
          }),
        100,
      );
    }
  }, [isTvMode, setView, setCalView, view]);

  const GROUP_BY_CONTROL_ID = "control_groupby";

  const groupByControlResource = useMemo(
    () =>
      isTimelineView && !isTvMode
        ? [
          {
            id: GROUP_BY_CONTROL_ID,
            name: "Equipment – Group by",
            isControl: true,
            eventCreation: false,
            eventDragBetweenResources: false,
            eventDragInTime: false,
            eventResize: false,
            reorder: false,
          },
        ]
        : [],
    [isTimelineView, isTvMode],
  );

  /** Custom navigation functions that move by one unit (day/week/month) */
  const navigateToPrev = useCallback(() => {
    if (!calendarInstance || !timelineSelectedDate) return;

    const currentDate = moment(timelineSelectedDate);
    let newDate;

    switch (view) {
      case TIMELINE_TYPE.DAY:
        // Move backward by 1 day
        newDate = currentDate.clone().subtract(1, "days");
        break;
      case TIMELINE_TYPE["3DAY"]:
        // Move backward by 1 day
        newDate = currentDate.clone().subtract(1, "days");
        break;
      case TIMELINE_TYPE.WEEK:
      case TIMELINE_TYPE.MONTH:
        // Move backward by 1 month
        newDate = currentDate.clone().subtract(1, "months");
        break;
      default:
        newDate = currentDate.clone().subtract(1, "days");
    }

    setTimelineSelectedDate(newDate.toDate());
    setTimeout(() => {
      calendarInstance.navigate(newDate.toDate());
    }, 0);
  }, [calendarInstance, timelineSelectedDate, view]);

  const navigateToNext = useCallback(() => {
    if (!calendarInstance || !timelineSelectedDate) return;

    const currentDate = moment(timelineSelectedDate);
    let newDate;

    switch (view) {
      case TIMELINE_TYPE.DAY:
        // Move forward by 1 day
        newDate = currentDate.clone().add(1, "days");
        break;
      case TIMELINE_TYPE.WEEK:
      case TIMELINE_TYPE.WORKWEEK:
        // Move forward by 1 week
        newDate = currentDate.clone().add(1, "weeks");
        break;
      case TIMELINE_TYPE.MONTH:
        // Move forward by 1 month
        newDate = currentDate.clone().add(1, "months");
        break;
      default:
        newDate = currentDate.clone().add(1, "days");
    }

    setTimelineSelectedDate(newDate.toDate());
    setTimeout(() => {
      calendarInstance.navigate(newDate.toDate());
    }, 0);
  }, [calendarInstance, timelineSelectedDate, view]);

  const navigateToToday = useCallback(() => {
    if (!calendarInstance) return;

    const now = moment().tz(siteTimezone);
    let today;

    switch (view) {
      case TIMELINE_TYPE.WEEK:
      case TIMELINE_TYPE["3DAY"]:
      case TIMELINE_TYPE.WORKWEEK:
        // For week view, use current date (not start of week) so the range centers properly
        today = now.clone().toDate();
        break;
      case TIMELINE_TYPE.MONTH:
        // For month view, use current date (not start of month) so the range centers properly
        today = now.clone().toDate();
        break;
      default:
        // For day view, use current day
        today = now.clone().toDate();
    }

    setTimelineSelectedDate(today);
    setTimeout(() => {
      calendarInstance.navigate(today);
    }, 0);
  }, [calendarInstance, siteTimezone, view]);

  /**
   * Create Google Calendar resources for selected calendars
   *
   * @returns {Array} - Array of Google Calendar resources
   */
  const createGoogleCalendarResources = useCallback(() => {
    if (!isGoogleLoggedIn || isTvMode) {
      return [];
    }

    const resources = [];

    // Add personal calendar resources (from selected calendars)
    if (selectedCalendarIds.length) {
      selectedCalendarIds.forEach((calendarId) => {
        const calendar = calendarData[calendarId];
        if (!calendar || !calendar.checked) return;

        resources.push({
          id: `google_calendar_${calendarId}`,
          name: formatTitle(calendar.name),
          color: "#1E8787",
          eventCreation: false,
          fixed: true,
          eventDragBetweenResources: false,
          eventDragInTime: false,
          eventResize: false,
          eventOverlap: isRecurringBooking, // allow overlap for recurring bookings
          reorder: false
        });
      });
    }

    return resources;
  }, [isGoogleLoggedIn, selectedCalendarIds, calendarData, isTvMode, isRecurringBooking]);

  /**
   * Transform Google Calendar events to Mobiscroll format
   *
   * @param {Array} googleEvents - Events from Google Calendar
   * @returns {Array} - Transformed events for Mobiscroll
   */
  const transformGoogleEvents = useCallback((googleEvents) => {
    if (!googleEvents || !googleEvents.length) return [];

    const transformedEvents = [];

    googleEvents.forEach((event) => {
      // Skip working location events
      if (event.googleEvent?.eventType === "workingLocation") {
        return;
      }
      const eventTitle = event.title || event.summary || "Busy";
      const calendarId = event.googleCalendarId;

      // Use the specific calendar ID to assign the correct resource
      const resourceId = calendarId
        ? `google_calendar_${calendarId}`
        : "google_calendar_default";

      transformedEvents.push({
        id: `google_${event.id}_${calendarId || "default"}`,
        title: eventTitle,
        start: event.start,
        end: event.end,
        resource: resourceId,
        color: "#1E8787", // Set the specific color for Google Calendar events
        fcolor: "#fff", // White font color
        allDay: event.allDay || false,
        readonly: true,
        cssClass: "google-calendar-event",
        googleCalendarId: calendarId, // Keep the original calendar ID for reference
        type: "GoogleCalendar", // Add type to identify Google Calendar events
      });
    });

    return transformedEvents;
  }, []);

  /**
   * Load Google Calendar events for the current view period Splits large date
   * ranges (>90 days) into smaller chunks and makes async requests
   */
  const loadGoogleCalendarEvents = useCallback(async () => {
    if (
      isTvMode ||
      !isGoogleLoggedIn ||
      !selectedCalendarIds.length ||
      !timelineSelectedDate
    ) {
      return;
    }

    try {
      let startDate, endDate;
      const currentDate = moment(timelineSelectedDate);
      
      // Calculate date range based on current view
      switch (view) {
        case TIMELINE_TYPE.DAY:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
          break;
        case TIMELINE_TYPE["3DAY"]:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS["3DAY"], "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS["3DAY"], "days")
            .toDate();
          break;
        case TIMELINE_TYPE.WEEK:
        case TIMELINE_TYPE.WORKWEEK:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.WEEK, "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.WEEK, "days")
            .toDate();
          break;
        case TIMELINE_TYPE.MONTH:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.MONTH + 1, "months")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.MONTH + 1, "months")
            .toDate();
          break;
        default:
          startDate = currentDate
            .clone()
            .subtract(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
          endDate = currentDate
            .clone()
            .add(TIMELINE_BUFFER_UNITS.DAY, "days")
            .toDate();
      }

      // Load Google Calendar events for the calculated date range
      // The GoogleCalendarContext will handle chunking automatically if needed
      await loadCalendarEvents(selectedCalendarIds, startDate, endDate);
    } catch (error) {
      console.error("Failed to load Google Calendar events:", error);
    }
  }, [
    isGoogleLoggedIn,
    selectedCalendarIds,
    timelineSelectedDate,
    view,
    loadCalendarEvents,
    isTvMode,
  ]);

  // Update Google Calendar resources when calendar selection changes
  useEffect(() => {
    const googleResources = createGoogleCalendarResources();
    setGoogleCalendarResources(googleResources);
  }, [createGoogleCalendarResources]);

  // Load Google Calendar events when view changes or calendar selection changes
  useEffect(() => {
    loadGoogleCalendarEvents();
  }, [loadGoogleCalendarEvents]);

  /**
   * Render header for timeline
   *
   * @returns - Template for event header
   */
  const renderMyHeader = () => {
    return (
      <>
        {!isTvMode && (
          <>
            <div
              className="md-work-week-today custom-nav-button today-button"
              onClick={navigateToToday}
              title="Go to today"
            >
              Today
            </div>
             <OwcIconButton
              flat={true}
              onClick={navigateToPrev}
              icon="chevron_left"
              elevated={false}
              disableRipple={true}
              className="md-work-week-prev mobi-custom-nav-button"

            />
            <OwcIconButton
              flat={true}
              onClick={navigateToNext}
              icon="chevron_right"
              elevated={false}
              disableRipple={true}
              className="md-work-week-prev mobi-custom-nav-button"/>
            <CalendarNav className="md-work-week-nav"/>
            {/* Only show view selector for non-equipment views */}
            <div style={{ marginLeft: "auto" }}>
              <OwcSelectDropdown
                style={{ width: 154 }}
                size="compact"
                outlined
                onSelectChange={(ev) => {
                  const selectedIndex = ev.detail[0];
                  if (selectedIndex === 0) {
                    changeView(TIMELINE_TYPE.DAY);
                  }
                  if (selectedIndex === 1) {
                    changeView(TIMELINE_TYPE["3DAY"]);
                  }
                  if (selectedIndex === 2) {
                    changeView(TIMELINE_TYPE.WEEK);
                  }
                  if (selectedIndex === 3) {
                    changeView(TIMELINE_TYPE.MONTH);
                  }
                }}
              >
                <OwcListItem selected={view === TIMELINE_TYPE.DAY}>
                  Day
                </OwcListItem>
                <OwcListItem selected={view === TIMELINE_TYPE["3DAY"]}>
                  3 Days
                </OwcListItem>
                <OwcListItem selected={view === TIMELINE_TYPE.WEEK}>
                  Week
                </OwcListItem>
                <OwcListItem selected={view === TIMELINE_TYPE.MONTH}>
                  Month
                </OwcListItem>
              </OwcSelectDropdown>
            </div>
          </>
        )}
      </>
    );
  };

  // FIXME: This medthod should return a class when avaiable events are rendered
  /**
   * To apply css class dynamically for events
   *
   * @param {events} event - Has event details for that hour
   * @returns - EventOccurrence
   */
  const getHourStyle = useCallback(
    (events) => {
      let hourEventClass = {};
      events.forEach((element) => {
        if (
          isEditMode &&
          (element.bookingId === editBookingData.id ||
            element.bookingId === null)
        ) {
          const row = IconStatus.find((x) => x.text === element.type);
          if (row !== undefined) {
            hourEventClass = { background: row.backgroundColor };
          }
        } else {
          if (element.type === BOOKING_TYPE.AVAILABLE) {
            const row = IconStatus.find((x) => x.text === element.type);
            if (row !== undefined) {
              hourEventClass = { background: row.backgroundColor };
            }
          }
        }
      });
      return hourEventClass;
    },
    [isEditMode, editBookingData],
  );

  /**
   * Change the date of timeline on the selected value in datepicker
   *
   * @param {any} param - Selected Date and time
   * @returns - Set date and time in timeline
   */
  const handleDateChange = useCallback(
    async (param, fromCancelAction = false) => {
      const reason = dpCloseReasonRef.current;
      dpCloseReasonRef.current = null;
      if (selectedList?.length > 0 && reason === 'cancel') {
        const pendingResourceIds = new Set(
          (pendingReservations || []).map((r) => r?.resource).filter(Boolean)
        );
        const everySelectedHasPending = selectedList.every((id) => pendingResourceIds.has(id));
        if (fromCancelAction && everySelectedHasPending) {
          return;
        }
      }

      if (param?.value) {
        const dateArray = param.value;
        const startDate = dateArray[0];
        const endDate = dateArray[1];
        let onGoingEventList = [];
        if (startDate && endDate) {
          if (isEditMode && isOngoing) {
            onGoingEventList = pendingReservations.filter(
              (x) => x.isOngoingEventFlag === true,
            );
          }
          clearPendingReservation();
          dateChangeInProgressRef.current = true;
          setLoading(true);
          setSelectedEndDate(endDate);
          setSelectedStartDate(startDate);
          const reservationResult = await getReservationBooking(
            changeDateToUtcString(startDate),
            changeDateToUtcString(endDate),
          );
          if (reservationResult.hasError) {
            setLocalFetchError(true);
            setLoading(false);
            dateChangeInProgressRef.current = false;
            return;
          }
          const reservationData = reservationResult.data;
          getEventDetails({
            startDate: changeDateToUtcString(startDate),
            endDate: changeDateToUtcString(endDate),
            reservationList: [...reservationData, ...onGoingEventList],
            onGoingEventList,
            equipmentListOverride: null,
            isQuickEdit: false,
            includeNewEquipmentInEditMode: isEditMode,
          });
          setTimelineSelectedDate(moment(startDate).tz(siteTimezone).toDate());
          const truncateStartDate = new Date(startDate)
            .toLocaleString("en-US", {
              timeZone: displayTimeZone,
            })
            .split(",")[0];
          const truncateEndDate = new Date(endDate)
            .toLocaleString("en-US", {
              timeZone: displayTimeZone,
            })
            .split(",")[0];
          if (truncateStartDate === truncateEndDate) {
            changeView(TIMELINE_TYPE.DAY);
          } else {
            changeView(TIMELINE_TYPE.WEEK);
          }
          setLoading(false);
          dateChangeInProgressRef.current = false;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStartDate, selectedEndDate, pendingReservations],
  );

  /**
   * Call when date change in timeline
   *
   * @param {any} event - Selected Date
   * @param {any} inst - Selected instance
   * @returns - Set date and time in timeline
   */
  const onSelectedDateChange = useCallback(
    (event) => {
      if (!event.date) return;

      // FIXME: find a better solution if possible since using internal function _active is not best practice
      // refDate is a known Mobiscroll bug when selectedDate and refDate are far apart
      // because Mobiscroll internally tracks the correct selected date in _active
      // but its event.date calculation uses the buggy page/grid offset logic from refDate,
      // so we can't rely on it to get the correct date
      const correctDate = event.inst?._viewDate
        ? new Date(event.inst._viewDate)
        : event.date;

      // Only update if this is a user interaction, not initial mount
      // Check if the new date is significantly different (not just time stripped)
      if (timelineSelectedDate) {
        const currentDay = moment(timelineSelectedDate).startOf("day");
        const newDay = moment(correctDate).startOf("day");
        // Only update if day actually changed (user navigation)
        if (!currentDay.isSame(newDay)) {
          setTimelineSelectedDate(correctDate);
        }
      } else {
        // First time setting, allow it
        setTimelineSelectedDate(correctDate);
      }
    },
    [timelineSelectedDate],
  );

  /**
   * Enforce fixed number of columns in timeline view based on current view type
   *
   * @param {Eventcalendar} inst - Calendar instance
   * @param {string} view - Current view day, 3day, week, month.
   * @param {boolean} isTvMode - TV mode flag
   */
  const setupColumnWidth = useCallback((inst, view, isTvMode) => {
    const visibleColumns = (isTvMode ? 14 : VIEW_COLUMNS[view]) || 0;
    if (!visibleColumns) return;

    const calendarEl = inst?._el;
    const gridScroll = calendarEl.querySelector(".mbsc-timeline-grid-scroll");
    const resourceScroll = calendarEl.querySelector(".mbsc-timeline-resources");
    const resourceWidth = resourceScroll ? resourceScroll.clientWidth : 0;
    const viewportWidth = gridScroll.clientWidth - resourceWidth;

    const columnWidth = Math.max(1, Math.floor(viewportWidth / visibleColumns));
    const styleId = `mobiscroll-column-override-${inst?._uid || "default"}`;
    let styleSheet = document.getElementById(styleId);
    if (!styleSheet) {
      styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      document.head.appendChild(styleSheet);
    }

    styleSheet.innerHTML = `
          .md-timeline-template .mbsc-timeline-column,
          .md-timeline-template .mbsc-timeline-day.mbsc-timeline-column {
            width: ${columnWidth}px !important;
            min-width: ${columnWidth}px !important;
            max-width: ${columnWidth}px !important;
          }

          .md-timeline-template .mbsc-timeline-header-column,
          .md-timeline-template .mbsc-timeline-header-week,
          .md-timeline-template .mbsc-timeline-header-week-text {
            width: ${columnWidth}px !important;
            min-width: ${columnWidth}px !important;
            max-width: ${columnWidth}px !important;
          }
        `;
  }, []);

  /** Sets or update date picker */
  const updateDatePicker = useCallback(
    (pendingReservationsForPicker) => {
      pendingReservationsForPicker = pendingReservationsForPicker.filter(
        (reservation) =>
          reservation.start &&
          reservation.end &&
          (!isDuplicate || !reservation.bookingId),
      );
      const starts = sortBy(pendingReservationsForPicker, ["start"]);
      const ends = sortBy(pendingReservationsForPicker, ["end"]);
      slotUpdateInProgressRef.current = true;
      setSelectedStartDate(starts[0]?.start || null);
      setSelectedEndDate(ends[ends?.length - 1]?.end || null);

      if (!starts[0]?.start || !ends[ends?.length - 1]?.end) {
        resetAll();
      }
    },
    [isDuplicate, resetAll, setSelectedEndDate, setSelectedStartDate],
  );

  const syncDatePickerFromPendingReservations = useCallback(() => {
    if (
      shouldSyncDatePickerFromPendingReservations({
        selectedStartDate,
        selectedEndDate,
        pendingReservations,
      })
    ) {
      updateDatePicker(pendingReservations);
    }
  }, [pendingReservations, selectedEndDate, selectedStartDate, updateDatePicker]);

  const updateEquipmentCapabilitiesForRange = useCallback(
    async (timelineStartDay, timelineEndDay) => {
      if (!resourceData || resourceData.length === 0 || !user) {
        return;
      }

      try {
        await checkAllEquipmentPermissions();

        const availabilityChecks = await Promise.all(
          resourceData.map(async (resource) => {
            const inventoryId = resource.id || resource.inventoryId;
            if (!inventoryId) return null;

            const availability = await checkEquipmentAvailability(
              inventoryId,
              timelineStartDay,
              timelineEndDay,
            );

            return {
              inventoryId,
              ...availability,
            };
          }),
        );

        setEquipmentBookingCapabilities((prevCapabilities) => {
          const nextCapabilities = new Map(prevCapabilities);

          availabilityChecks.forEach((check) => {
            if (!check?.inventoryId) {
              return;
            }

            const currentCapabilities = nextCapabilities.get(check.inventoryId) || {};
            nextCapabilities.set(check.inventoryId, {
              ...currentCapabilities,
              hasConflicts: check.hasConflicts,
              existingBookings: check.existingBookings,
              availableSlots: check.availableSlots,
            });
          });

          return nextCapabilities;
        });
      } catch (error) {
        console.error(
          "Error checking equipment permissions and availability:",
          error,
        );
      }
    },
    [checkAllEquipmentPermissions, checkEquipmentAvailability, resourceData, user],
  );

  const handleTimelinePageLoading = useCallback(
    async (event) => {
      setLoading(true);
      const { timelineStartDay, timelineEndDay } = getTimelineBoundsFromPageEvent(event);

      try {
        await updateEquipmentCapabilitiesForRange(timelineStartDay, timelineEndDay);

        const reservationResult = await getReservationBooking(
          timelineStartDay,
          timelineEndDay,
        );

        if (!reservationResult || reservationResult.hasError) {
          setLocalFetchError(true);
          return;
        }

        const reservationData = reservationResult.data || [];
        setEventsData((prevEvents) =>
          mergeTimelineReservations({ prevEvents, reservationData }),
        );

        syncDatePickerFromPendingReservations();
      } finally {
        setLoading(false);
      }
    },
    [
      getReservationBooking,
      setEventsData,
      setLoading,
      syncDatePickerFromPendingReservations,
      updateEquipmentCapabilitiesForRange,
    ],
  );

  const handleStandardPageLoading = useCallback(
    async (event) => {
      if (
        shouldSkipStandardPageLoading({
          isDateChangeInProgress: dateChangeInProgressRef.current,
          isSlotUpdateInProgress: slotUpdateInProgressRef.current,
        })
      ) {
        slotUpdateInProgressRef.current = false;
        return;
      }

      setLoading(true);
      setEventsData([]);
      const { timelineStartDay, timelineEndDay } = getTimelineBoundsFromPageEvent(event);

      try {
        const reservationResult = await getReservationBooking(
          timelineStartDay,
          timelineEndDay,
        );

        if (!reservationResult || reservationResult.hasError) {
          setLocalFetchError(true);
          return;
        }

        const reservationData = reservationResult.data || [];

        if (
          shouldLoadLatestEventDetails({
            newEquipmentIds,
            selectedStartDate,
            selectedEndDate,
          })
        ) {
          getLatestEventDetailsOnly(
            getLatestEventDetailsStartDate({
              isOngoing,
              isEditMode,
              siteTimezone,
              originalSelectedStartDate,
              selectedStartDate,
            }),
            changeDateToUtcString(originalSelectedEndDate ?? selectedEndDate),
            reservationData,
            newEquipmentIds,
          );
          setNewEquipmentIds([]);
          return;
        }

        const nextEvents = buildStandardPageEvents({
          reservationData,
          pendingReservations,
        });

        setEventsData(() => nextEvents);
        syncDatePickerFromPendingReservations();
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations, selectedStartDate, selectedEndDate, newEquipmentIds, isOngoing, isEditMode],
  );

  const onPageLoading = useCallback(
    async (event, _inst) => {
      if (loading) return;

      if (isTimelineView) {
        await handleTimelinePageLoading(event);
        return;
      }

      await handleStandardPageLoading(event);
    },
    [
      handleStandardPageLoading,
      handleTimelinePageLoading,
      isTimelineView,
      loading,
    ],
  );

  /**
   * Render Custom Hour for timeline
   *
   * @returns - Template for Custom Hour
   */
  const renderCustomHour = (args) => {
    return (
      <div className={"md-date-header-hour "} style={getHourStyle(args.events)}>
        {formatDate("h:mm A", args.date)}
      </div>
    );
  };

  /**
   * Render event icons based on booking type
   *
   * @param {string} type - Event type
   * @returns - Icons for events
   */
  const renderBookingIcon = (type, eventData) => {
    // Use the same function as PopoverBookingTitle component
    // to maintain consistency across the application
    const displayText = getBookingDisplayText({
      type: type,
      assistanceDeclaration: eventData?.assistanceDeclaration,
    });
    const iconDetails = IconStatus.find((item) => item.text === displayText);

    if (iconDetails?.icon) {
      return iconDetails.icon;
    }

    // Fallback to category lookup if no icon found by display text
    const category = getBookingCategory(type);
    const iconName = IconStatus.find((item) => item.text === category)?.icon;
    if (iconName) {
      return iconName;
    }
    //FIXME: below left for backward compatibility with old bookings
    switch (type) {
      case BOOKING_TYPE.BOOKED:
        return "schedule_on";
      case BOOKING_TYPE.RESERVED:
        return "order_approval";
      case BOOKING_TYPE.MAINTENANCE:
        return "test_result";
      case BOOKING_TYPE.AVAILABLE:
        return "confirm";
      default:
        return "confirm";
    }
  };

  /**
   * Render Custom events for timeline
   *
   * @returns - Template for Custom events
   */
  const renderCustomScheduleEvent = (data) => {
    // Use custom equipment event renderer if available and in equipment view
    if (isTimelineView && renderCustomEquipmentEvent) {
      return renderCustomEquipmentEvent(data);
    }

    return (
      <TimelineBookingEvent
        data={data}
        eventHover={eventHover}
        setEventHover={setEventHover}
        usersList={usersList}
        isEditMode={isEditMode}
        editBookingData={editBookingData}
        isDuplicate={isDuplicate}
        eventSlotDelete={eventSlotDelete}
        manipulatePopoverContent={manipulatePopoverContent}
        bookingPopUpDispatchAction={dispatchPopupWithLoader}
        handleOnClose={handleOnClose}
        renderBookingIcon={renderBookingIcon}
        view={view}
      />
    );
  };

  /**
   * To handle overlap of events in time line
   *
   * @param {any} event - Args of created event
   * @param {inst} inst - Instanse of the event calendar
   * @returns - Boolean based on filterred events length
   */
  const hasOverlap = useCallback((event, inst) => {
    const events = inst
      .getEvents(event.start, event.end)
      .filter((e) => e.id !== event.id && e.resource === event.resource);
    return events.length > 0;
    // eslint-disable-next-line
  }, []);
  /**
   * To handle past events in time line
   *
   * @param {any} args - Args of created event
   * @returns - Boolean based on filterred events length
   */
  const hasPastEvent = useCallback(
    (args) => {
      const now = moment.tz(siteTimezone);
      const eventStart = moment(args.event.start).tz(siteTimezone);
      const eventEnd = moment(args.event.end).tz(siteTimezone);
      if (eventEnd.isSameOrBefore(now)) return true;
      const currentSlotStart = now
        .clone()
        .seconds(0)
        .milliseconds(0)
        .minutes(Math.floor(now.minutes() / 30) * 30);
      return eventStart.isBefore(currentSlotStart);
    }, [siteTimezone]);
  /**
   * To handle past events in time line
   *
   * @param {any} args - Args of created event
   * @returns - Boolean based on filterred events length
   */
  const hasPastEndDate = useCallback(
    (args) => {
      const currentTime = moment.tz(siteTimezone);
      const eventEndTime = moment(args.event.end).tz(siteTimezone);
      return currentTime.isAfter(eventEndTime);
      // eslint-disable-next-line
    },
    [siteTimezone],
  );

  useEffect(() => {
    if (!isTimelineView) {
      setGroupBy("EQUIPMENT");
    }
  }, [isTimelineView]);

  const popupText = (
    <OwcTypography>
      Please select a valid date range in the future. <br />
      Adjusting past dates/time is not allowed.
    </OwcTypography>
  );

  const dispatchPopupWithLoader = useCallback(
    (popupAction) => {
      bookingPopUpDispatchAction({
        type: "loading",
        payload: { loading: true },
      });

      const dispatchPopup = () => {
        bookingPopUpDispatchAction(popupAction);
        bookingPopUpDispatchAction({
          type: "loading",
          payload: { loading: false },
        });
      };

      setTimeout(dispatchPopup, 0);
    },
    [bookingPopUpDispatchAction],
  );

  const handleOnClose = () => {
    bookingPopUpDispatchAction({
      type: "reset",
    });
  };
  /**
   * To render the model name with close icon in mobiscroll calendar
   *
   * @returns {component} - Model name with cross icon
   */
  const customResource = (resource) => {
    // Check if this is a Google Calendar resource
    const isGoogle = resource.id?.startsWith("google_calendar_");
    const isControl = resource.isControl && resource.id === GROUP_BY_CONTROL_ID;
    const isGroup =
      (Array.isArray(resource.children) && resource.children.length > 0) ||
      String(resource.id || "").startsWith("grp_");
    const rowClass = `md-resource-header-template-cont ${isControl
      ? "md-row-control"
      : isGoogle
        ? "md-row-google"
        : isGroup
          ? "md-row-group"
          : "md-row-leaf"
      }`;

    if (isControl) {
      const totalEquipment = leafResources.length || 0;

      return (
        <div className={rowClass}>
          <div
            className="md-resource-header-template-title"
            style={{ cursor: "pointer", width: "100%" }}
            onClick={() => setEquipmentHdrOpen((v) => !v)}
          >
            <div
              className="md-resource-header-template-name"
              style={{ width: "100%" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
                id={`attr_groupby_tooltip`}
              >
                <OwcTypography variant="body2">Equipment details </OwcTypography>
                <OwcBadge
                  type="active"
                  style={{ marginLeft: "auto", width: 24, height: 24 }}
                >
                  {totalEquipment}
                </OwcBadge>
                <OwcIcon
                  name={isEquipmentHdrOpen ? "chevron_up" : "chevron_down"}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <OwcTooltip
                anchor={`attr_groupby_tooltip`}
                style={{ display: isEquipmentHdrOpen ? "none" : "block" }}
              >
                Click to toggle grouping
              </OwcTooltip>
            </div>
          </div>

          {isEquipmentHdrOpen && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {renderGroupByDropdown()}
            </div>
          )}
        </div>
      );
    }

    if (isGoogle) {
      // Extract the calendar ID from the resource ID
      const calendarId = resource.id.replace("google_calendar_", "");

      return (
        <div className={rowClass}>
          <div className="md-resource-header-template-name">
            <div style={{ display: "flex", alignItems: "center" }}>
              <OwcTypography
                variant="body2"
                style={{
                  marginRight: "10px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {resource.name}
              </OwcTypography>

              {/* X button to deselect Google Calendar */}
              <OwcIconButton
                icon="circle_clear"
                type="filled"
                flat
                style={{
                  marginLeft: "auto",
                  fontSize: "16px",
                }}
                onClick={(ev) => {
                  // Use toggleCalendar to deselect this calendar
                  toggleCalendar(calendarId, false);
                  ev.stopPropagation();
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    // Regular equipment resource handling
    const permissions = equipmentPermissions.get(resource.id);
    const capabilities = equipmentBookingCapabilities.get(resource.id);
    const canBook = permissions?.canBook && capabilities?.canCreate;
    const reason = permissions?.reason || capabilities?.reason;

    // Check if this is admin-only access
    const isAdminAccess =
      canBook &&
      user?.roles?.some((role) => role === "BookingAdmin") &&
      reason?.includes("Admin access required");

    return (
      <div className={rowClass}>
        <div className="md-resource-header-template-name">
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer"}}
            onClick={() => {
              manipulatePopoverContent({
                userSelectionType: RESOURCE,
                resourceId: resource?.id,
              });
            }}
          >
            <OwcTypography
              variant="body2"
              style={{
                marginRight: "10px",
                fontSize: '14px',
                lineHeight: isGroup? '165%':'140%',
                fontFamily: 'Roche Sans',
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: !isTimelineView
                  ? "inherit"
                  : canBook || isGroup
                    ? "inherit"
                    : "#999",
                fontStyle: isAdminAccess ? "italic" : "normal",
              }}
              id={resource.id}
            >
              {resource.name}
              {isAdminAccess && (
                <span
                  style={{
                    fontSize: "10px",
                    color: "#FF9800",
                    fontWeight: "bold",
                    marginLeft: "4px",
                  }}
                >
                  (Admin)
                </span>
              )}
            </OwcTypography>
            <OwcTooltip anchor={resource.id}>{resource.name}</OwcTooltip>

            {/* Permission status indicator for equipment view */}
            {isTimelineView && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "auto",
                  marginRight: "8px",
                }}
              >
                {canBook ? (
                  <OwcIcon
                    name="check_circle"
                    style={{
                      color: isAdminAccess ? "#FF9800" : "#4CAF50",
                      fontSize: "16px",
                    }}
                    title={
                      isAdminAccess
                        ? "Bookable (Admin access only)"
                        : "You can book this equipment"
                    }
                  />
                ) : (
                  <OwcIcon
                    name="block"
                    style={{ color: "#F44336", fontSize: "16px" }}
                    title={`Cannot book: ${reason || "No permission"}`}
                    s
                  />
                )}
              </div>
            )}

            {!isTimelineView && (
              <OwcIconButton
                icon="circle_clear"
                type="filled"
                flat
                style={{ marginLeft: "auto" }}
                onClick={(ev) => {
                  const isAnyOnGoingEvent = pendingReservations.filter(
                    (x) => x.resource === resource?.id && x.isOngoingEventFlag,
                  );
                  if (isEditMode && isAnyOnGoingEvent.length > 0) {
                    dispatchPopupWithLoader({
                      type: "onGoingBooking",
                      payload: {
                        onApprove: handleOnClose,
                      },
                    });
                  } else {
                    dispatchPopupWithLoader({
                      type: "futureBooking",
                      payload: {
                        onApprove: () => handlerOnDelete(resource?.id),
                        onclose: handleOnClose,
                      },
                    });
                  }
                  setDeleteResourceId(resource?.id);
                  setCancelStatus(true);
                  ev.stopPropagation();
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * To show the number of selected events for each equipment
   *
   * @returns {JSX.Element} - Show the number of selected events with model
   *   header
   */
  const customResourceHeader = () => {
    const totalEquipment = leafResources?.length || 0;
    const totalGoogleCalendars = googleCalendarResources?.length || 0;

    return (
      <>
        {/* Google Calendar section header */}
        {!isTvMode && (totalGoogleCalendars > 0 || isTimelineView) && (
          <div className="md-resource-header-template-title">
            <div className="md-resource-header-template-name">
              <div style={{ display: "flex", alignItems: "center" }}>
                <OwcTypography variant="body2" style={{ marginRight: "10px" }}>
                  Calendar details
                </OwcTypography>
                <OwcBadge
                  type="active"
                  style={{
                    marginLeft: "auto",
                    width: "24px",
                    height: "24px",
                    backgroundColor:
                      totalGoogleCalendars > 0 ?? "#A0A0A0",
                  }}
                >
                  {totalGoogleCalendars || 0}
                </OwcBadge>
              </div>
            </div>
          </div>
        )}

        {/* Equipment section header */}
        {totalEquipment > 0 && (!isTimelineView || isTvMode) && (
          <div className="md-resource-header-template-title">
            <div className="md-resource-header-template-name">
              <div style={{ display: "flex", alignItems: "center" }}>
                <OwcTypography variant="body2" style={{ marginRight: "10px" }}>
                  Equipment details
                </OwcTypography>
                <OwcBadge
                  type="active"
                  style={{
                    marginLeft: "auto",
                    width: "22px",
                    height: "21px",
                  }}
                >
                  {totalEquipment}
                </OwcBadge>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const hasHiddenOverlap = async (event) => {
    const inventoryId = event.resource;

    // +1h for comparisons (the selected event is shifted one hour backward)
    const adjustedStart = new Date((event?.start instanceof Date ? event.start : new Date(event?.start)).getTime() + 3600000);
    const adjustedEnd = new Date((event?.end instanceof Date ? event.end : new Date(event?.end)).getTime() + 3600000);

    try {
      const res = await client.query({
        query: GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
        variables: {
          site: user?.site,
          inventoryId: { eq: inventoryId },
          limit: 1000,
        },
        dataPath: ["data", "bookingEntriesBySiteAndInventoryId"],
        fetchPolicy: "no-cache",
      });

      if (res?.errors && res.errors.length > 0) {
        throw new Error("GraphQL validation error");
      }

      const bookingEntries = res?.data?.bookingEntriesBySiteAndInventoryId;
      const activeBookings = bookingEntries?.items?.filter((entry) => {
        if (entry.active) {
          return entry.start < adjustedEnd && entry.end > adjustedStart;
        }
        return false;
      });

      return { hasOverlap: activeBookings.length > 0, hasError: false };
    } catch (err) {
      console.error("hasHiddenOverlap query error", err);
      return { hasOverlap: false, hasError: true };
    }
  };

  /**
   * Event triggered when a timeline event is created
   *
   * @param {any} args - Args of created event
   * @param {inst} args - Instanse of the event calendar
   * @returns - False if validation fails
   */
  const onEventCreate = useCallback(
    (args, inst) => {
      if (isTvMode) return;

      if (hasServerError) {
        snackbarService.show({
          message: "Unexpected Server Error",
          type: "error",
        });
        return false;
      }

      const resourceId = args.event.resource;

      // Check if in equipment view and validate permissions
      if (isTimelineView) {
        const permissions = equipmentPermissions.get(resourceId);
        const capabilities = equipmentBookingCapabilities.get(resourceId);

        if (!permissions?.canBook || !capabilities?.canCreate) {
          const reason =
            permissions?.reason ||
            capabilities?.reason ||
            "No permission to book this equipment";
          toast({
            message: `Cannot create booking: ${reason}`,
          });
          return false;
        }
      }

      if (hasPastEvent(args)) {
        toast({
          message: "Past date not allowed",
        });
        return false;
      }
      if (hasOverlap(args.event, inst)) {
        toast({
          message: "Overlap not allowed",
        });
        return false;
      }

      // Perform async validation for hidden overlaps only in equipment view
      const eventId = uniqueId("available_");

      let eventRow = {
        ...singleEventSkeleton,
        start: changeDateToUtcString(args.event.start),
        end: changeDateToUtcString(args.event.end),
        resource: args.event.resource,
        id: eventId,
      };

      // Always create new events as "Available" type regardless of edit mode or duplication
      // This ensures the correct blue color for new bookings
      const availableRow = IconStatus.find(
        (x) => x.text === BOOKING_TYPE.AVAILABLE,
      );
      if (availableRow !== undefined) {
        eventRow = {
          ...eventRow,
          type: BOOKING_TYPE.AVAILABLE,
          color: availableRow.backgroundColor,
          fcolor: availableRow.color,
          title: BOOKING_TYPE.AVAILABLE,
        };
      }

      // Only check for hidden overlaps in equipment view
      if (isTimelineView) {
        // Check for hidden overlaps asynchronously after allowing the event
        hasHiddenOverlap(args.event)
          .then((result) => {
            if (result.hasError) {
              snackbarService.show({
                message: "Unexpected Server Error",
                type: "error",
              });
              setLocalFetchError(true);
              return;
            }

            if (result.hasOverlap) {
              toast({
                message: "Overlap not allowed - hidden events detected",
              });

              // Remove the event that was just created
              const filteredReservations = pendingReservations.filter(
                (item) => item.id !== eventId,
              );
              updatePendingReservation(filteredReservations);
              setEventsData((prev) =>
                prev.filter((item) => item.id !== eventId),
              );
              updateDatePicker(filteredReservations);

              return;
            }

            // If no hidden overlap, proceed with adding the event
            const newPendingReservations = [...pendingReservations, eventRow];
            updatePendingReservation(newPendingReservations);
            setEventsData((prev) => [...prev, eventRow]);
            updateDatePicker(newPendingReservations);
          })
          .catch((error) => {
            console.error("Error checking hidden overlap:", error);
            snackbarService.show({
              message: "Unexpected Server Error",
              type: "error",
            });
            setLocalFetchError(true);
          });
      } else {
        // For non-equipment views, directly add the event without hidden overlap check
        const newPendingReservations = [...pendingReservations, eventRow];
        updatePendingReservation(newPendingReservations);
        setEventsData((prev) => [...prev, eventRow]);
        updateDatePicker(newPendingReservations);
      }

      // Return false to prevent the default event creation by MobiScroll
      // We handle the event creation manually above
      return false;
    },
    // eslint-disable-next-line
    [
      pendingReservations,
      selectedStartDate,
      selectedEndDate,
      isTimelineView,
      equipmentPermissions,
      equipmentBookingCapabilities,
      resourceData,
      isGroupView,
      hasServerError,
    ],
  );

  /**
   * Event triggered when a timeline event is updated
   *
   * @param {any} args - Args of created event
   * @param {inst} args - Instanse of the event calendar
   * @returns - False if validation fails
   */
  const onEventUpdate = useCallback(
    (args, inst) => {
      if (isDuplicate && !args.event.id?.startsWith("available_")) {
        return false;
      }
      if (hasOverlap(args.event, inst)) {
        toast({
          message: "Overlap not allowed",
        });

        return false;
      }
      if (isEditMode && args.event.isOngoingEventFlag) {
        if (
          moment(args.event.start).tz(siteTimezone).format() !==
          moment(args.oldEvent.start).tz(siteTimezone).format()
        ) {
          toast({
            message: "Can't change start date.",
          });
          return false;
        }
        if (hasPastEndDate(args)) {
          dispatchPopupWithLoader({
            type: "eventBooking",
            payload: {
              onApprove: handleOnClose,
              title: "Date range adjustment not permitted",
              content: popupText,
            },
          });
          return false;
        }
      } else {
        if (hasPastEvent(args)) {
          toast({
            message: "Past date not allowed",
          });
          return false;
        }
      }

      const tempReservation = pendingReservations.map((item) => {
        if (item.id === args.event.id) {
          item = {
            ...item,
            start: changeDateToUtcString(args.event.start),
            end: changeDateToUtcString(args.event.end),
            resource: args.event.resource,
          };
        }
        return item;
      });

      updateDatePicker(tempReservation);

      updatePendingReservation([...tempReservation]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations, selectedEndDate],
  );

  /**
   * Event triggered when a timeline event is deleted
   *
   * @param {any} args - Args of created event
   * @param {inst} args - Instanse of the event calendar
   * @returns - False if validation fails
   */
  const onEventDeleted = useCallback(
    (args) => {
      if (isTimelineView) {
        return false;
      }

      const tempReservation = pendingReservations.filter(
        (item) => item.id !== args.event.id,
      );
      // Always update date picker to clear it when no reservations remain
      updateDatePicker(tempReservation);
      updatePendingReservation(tempReservation);
      setEventsData((prev) => {
        return prev.filter((item) => item.id !== args.event.id);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations],
  );

  /**
   * Event triggered when a user manually delete event
   *
   * @param {id} args - Args of created event
   * @returns - False if validation fails
   */
  const eventSlotDelete = useCallback(
    (id) => {
      onEventDeleted({ event: { id: id } });
      setEventsData((prev) => prev.filter((x) => x.id !== id));
      const tempPendingReservations = pendingReservations.filter(
        (item) => item.id !== id,
      );
      updatePendingReservation(tempPendingReservations);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingReservations],
  );

  const handlerOnDelete = (deleteResourceId) => {
    handleOnClose();
    removeEquipmentAndReservation(deleteResourceId, pendingReservations);
  };
  const handleActiveDateChange = (ev) => {
    if (ev.active === "start" && isOngoing) {
      setTimeout(() => {
        rangeInst.setActiveDate("end");
      });
    }
  };
  const updateLoaderinDatePicker = (event) => {
    const firstDay = event.firstDay;
    const lastDay = event.lastDay;
    const dateList = eachDayOfInterval({
      start: firstDay,
      end: lastDay,
    });
    const tempLabelsData = dateList.map((item) => {
      return {
        date: `${moment(item).format("YYYY-MM-DD")}T00:00`,
        text: "...",
        color: "white",
      };
    });
    setLabelsData(tempLabelsData);
    setColorsData([]);
  };
  const getAvailability = async (ev) => {
    let startDate = moment.tz(siteTimezone).isAfter(moment(ev.firstDay))
      ? moment.tz(siteTimezone).startOf("day")
      : moment(ev.firstDay).startOf("day");
    let endDate = `${moment(startDate.format("YYYY-MM-DD")).endOf("month").format("YYYY-MM-DD")}T23:59:59Z`;

    let originalStartDate = null;
    let originalEndDate = null;
    let originalStartDateString = null;
    let originalEndDateString = null;
    let isDateSame = false;
    if (isEditMode) {
      originalStartDateString = moment(editBookingData?.dateFrom)
        .tz(siteTimezone)
        .format("YYYY-MM-DD");
      originalEndDateString = moment(editBookingData?.dateTo)
        .tz(siteTimezone)
        .format("YYYY-MM-DD");
      originalStartDate = editBookingData?.dateFrom;
      originalEndDate = editBookingData?.dateTo;
      isDateSame = isSameDayBooking(
        originalStartDate,
        originalEndDate,
        siteTimezone,
      );
    }
    const months = [];
    let count = 0;
    while (count < 3) {
      months.push({
        start: `${startDate.format("YYYY-MM-DD")}T00:00`,
        end: endDate,
      });
      startDate.add(1, "month").startOf("month");
      endDate = `${moment(startDate.format("YYYY-MM-DD")).endOf("month").format("YYYY-MM-DD")}T23:59:59Z`;
      count++;
    }

    let tempResult = [];
    await Promise.all(
      months.map(async (item) => {
        const { items } = await getAllData({
          client,
          query: FIND_BOOKABLE_INSTRUMENTS,
          drillData: true,
          fetchPolicy: "no-cache",
          variables: {
            siteTimezone: siteTimezone,
            startDate: item.start,
            endDate: item.end,
            inventoryIds: selectedList,
          },
          dataPath: ["data", "findBookableInstruments"],
        });
        tempResult.push(...items);
      }),
    );
    let tempResultColorsData = [];
    let tempResultLablesData = [];
    let tempDisableDateList = [];

    tempResult.forEach((item) => {
      if (item.availability === "NONE") {
        // for disabling date
        const localDate = cloneDeep(item.date);
        if (isOngoing) {
          tempDisableDateList.push({
            start: moment(originalStartDate)
              .tz(siteTimezone)
              .add(1, "minutes")
              .utc()
              .format(),
            end: moment.tz(siteTimezone).startOf("hour").utc().format(),
          });
        }

        if (isEditMode) {
          if (localDate === originalStartDateString) {
            tempResultColorsData.push({
              date: localDate,
            });
            if (!isDateSame) {
              tempDisableDateList.push({
                start: moment(originalStartDate)
                  .tz(siteTimezone)
                  .add(1, "hours")
                  .utc()
                  .format(),
                end: moment(originalStartDate)
                  .tz(siteTimezone)
                  .endOf("day")
                  .utc()
                  .format(),
              });
            }
          } else if (localDate === originalEndDateString && !isDateSame) {
            tempResultColorsData.push({
              date: localDate,
            });
            tempDisableDateList.push({
              start: moment(originalEndDate)
                .tz(siteTimezone)
                .add(1, "hours")
                .utc()
                .format(),
              end: moment(originalEndDate)
                .tz(siteTimezone)
                .endOf("day")
                .utc()
                .format(),
            });
          } else {
            tempResultColorsData.push({
              date: localDate,
            });
            tempDisableDateList.push(localDate);
          }
        } else {
          tempResultColorsData.push({
            date: item.date,
          });
          tempDisableDateList.push(localDate);
        }
      }
      if (item.availability !== "ALL" && item.availability !== "NONE") {
        tempResultLablesData.push({
          date: item.date,
          text: item.availability,
          color: "white",
          textColor: "#ED4A0D",
          cellCssClass: "custom-label-text",
        });
      }
    });

    setColorsData(tempResultColorsData);
    setLabelsData(tempResultLablesData);
    setDisableDateList(tempDisableDateList);
  };
  const onPageLoadingInDatePicker = useCallback(
    async (event) => {
      updateLoaderinDatePicker(event);
      await getAvailability(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStartDate],
  );

  useEffect(() => {
    if (!calendarInstance) return;

    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        changeView(view);

        if (calendarInstance.refresh) {
          calendarInstance.refresh();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [calendarInstance, view, isTvMode, setupColumnWidth, changeView]);

  // Update events with recurrance rule
  const eventDataWithRecurrance = useMemo(() => {
    return [...eventData, ...(!isTvMode ? transformGoogleEvents(googleEvents) : [])].map(data =>
      data.active || data.googleCalendarId ? data : {
        ...data,
        recurring: getTrimmedRRuleString(displayTimeZone)
      }
    );
  }, [eventData, getTrimmedRRuleString, googleEvents, isTvMode, transformGoogleEvents, displayTimeZone]);

  useEffect(() => {
    if (eventDataWithRecurrance?.length) {
      setEventDataContext(eventDataWithRecurrance)
    }
  }, [eventDataWithRecurrance, setEventDataContext]);

  // Combine Google Calendar resources with equipment resources and allow overlap for recurring bookings
  const resourcesCombined = useMemo(() => {
    return [
      ...(!isTvMode ? googleCalendarResources : []),
      ...groupByControlResource,
      ...groupedResources,
    ].map(res => ({
      ...res,
      eventOverlap: isRecurringBooking
    }));
  }, [googleCalendarResources, groupByControlResource, groupedResources, isRecurringBooking, isTvMode]);

  useEffect(() => {
    setRecoursesContext(resourcesCombined)
  }, [setRecoursesContext, resourcesCombined]);

  return (
    <>
      {visibleDatePicker && (
        <div
          className="customeventcalendar_datepickerInputStyle"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Datepicker
            id="datepicker-from-my-booking"
            controls={["calendar", "timegrid"]}
            ref={setRangeInst}
            labels={labelsData}
            colors={colorsData}
            onPageLoading={onPageLoadingInDatePicker}
            onInit={(event, inst) => {
              // check cond if it needs to be opened
              if (!(selectedStartDate && selectedEndDate)) {
                inst.open();
              }
            }}
            value={[temporaryStartDate, temporaryEndDate]}
            closeOnOverlayClick={false}
            inputStyle="outline"
            select="range"
            display="anchored"
            anchorAlign={alignDatePicker(viewportWidth, isEditMode, isDuplicate)}
            inRangeInvalid={isEditMode}
            rangeEndInvalid={isEditMode}
            headerText="Experiment date(range)"
            stepMinute={60}
            touchUi={"auto"}
            min={
              isEditMode
                ? isOngoing
                  ? selectedStartDate
                  : moment.tz(siteTimezone).startOf("hour").format()
                : moment.tz(siteTimezone).startOf("hour").format()
            }
            minRange={3600000}
            dateFormat="D-MMM-YYYY"
            startIcon={<OwcIcon name="date" type="outlined" />}
            onCellClick={handleOnCellClick}
            onTempChange={(event) => {
              const value = event?.value || [];
              if (JSON.stringify(value) !== JSON.stringify(temporarySelectedDates)) {
                isDatepickerCancelledRef.current = false;
                setTemporaryStartDate(value[0]);
                setTemporaryEndDate(value[1]);
                debouncedSetTempDates(value);
              }
            }}
            onClose={(param) => {
              if (isDatepickerCancelledRef.current) {
                isDatepickerCancelledRef.current = false;

                setTemporaryStartDate(originalSelectedStartDate);
                setTemporaryEndDate(originalSelectedEndDate);
                const restoredTempDates =
                  originalSelectedStartDate && originalSelectedEndDate
                    ? [originalSelectedStartDate, originalSelectedEndDate]
                    : [];
                debouncedSetTempDates(restoredTempDates);
              } else {
                if (
                  param?.value &&
                  (moment(param?.value[0]).utc().format() !==
                    moment(selectedStartDate).utc().format() ||
                    moment(param?.value[1]).utc().format() !==
                    moment(selectedEndDate).utc().format())
                ) {
                  if (isEditMode) {
                    const isDateChange = !(
                      moment(selectedStartDate).utc().format() ===
                      moment(param?.value[0]).utc().format() &&
                      moment(selectedEndDate).utc().format() ===
                      moment(param?.value[1]).utc().format()
                    );
                    if (!isDateChange || param?.value[0] === param?.value[1]) {
                      return false;
                    }
                    if (
                      isOngoing &&
                      moment(param?.value[1]).tz(siteTimezone).utc().format() <
                      moment.tz(siteTimezone).startOf("hour").utc().format()
                    ) {
                      return false;
                    }
                    if (isOngoing || isDuplicate) {
                      handleDateChange(param, true);
                    } else {
                      dispatchPopupWithLoader({
                        type: "eventBooking",
                        payload: {
                          onApprove: () => handleDateChange(param),
                          title: "Experiment range changed",
                          content: `You have selected a new experiment range.
                                  Please be aware that your old equipment timeslots are removed.`,
                          onclose: handleOnClose,
                        },
                      });
                    }
                  } else {
                    handleDateChange(param, true);
                  }
                } else {
                  setSelectedStartDate(
                    param?.value[1] ? param?.value[0] : param?.value[1],
                  );
                  setSelectedEndDate(param?.value[1]);
                }
              }
            }}
            onCancel={() => {
              isDatepickerCancelledRef.current = true;
              dpCloseReasonRef.current = "cancel";
              if (!timelineSelectedDate) {
                setTimelineSelectedDate(moment().tz(siteTimezone).format());
              }
            }}
            timezonePlugin={momentTimezone}
            dataTimezone={dataTimeZone}
            displayTimezone={displayTimeZone}
            buttons={["cancel", "set"]}
            onActiveDateChange={handleActiveDateChange}
            invalid={disableDateList}
          >
            {(!isEditMode || isDuplicate) && <RecurringBookingPicker />}
          </Datepicker>
        </div>
      )}
      <Eventcalendar
        theme="ios"
        height={"100%"}
        view={calView}
        resources={resourcesCombined}
        data={eventDataWithRecurrance}
        scroll={isTvMode ? "06:00" : undefined}
        renderHeader={renderMyHeader}
        renderResource={customResource}
        renderResourceHeader={customResourceHeader}
        renderHour={renderCustomHour}
        renderScheduleEvent={renderCustomScheduleEvent}
        ref={calendarRef}
        onInit={handleCalendarInit}
        onPageLoaded={(_args, inst) => {
          setupColumnWidth(inst, view, isTvMode);
        }}
        cssClass={`${isTimelineView ? "md-timeline-template equipment-view" : "md-timeline-template"}
            ${isTimelineView && !hasBookableEquipment() ? " no-bookable-equipment" : ""}
            ${view === "week" ? " md-timeline-week-view" : ""}
            md-timeline-consistent
            ${isTvMode ? " tv-mode" : ""}`}
        clickToCreate={!isTvMode &&!isRecurringBooking && !isDatepickerOpen ? "single" : false}
        dragToCreate={!isRecurringBooking && !isDatepickerOpen && !isTvMode}
        dragToMove={!isRecurringBooking && !isDatepickerOpen}
        dragToResize={!isRecurringBooking && !isDatepickerOpen}
        dragTimeStep={30}
        selectedDate={(() => {
          const base =
            timelineSelectedDate ?? moment().tz(siteTimezone).toDate();
          return isTvMode ? moment(base).startOf("day").toDate() : base;
        })()}
        refDate={(() => {
          const currentDate =
            timelineSelectedDate ?? moment().tz(siteTimezone).toDate();
          let refDate;
          if (isTvMode) return moment(currentDate).startOf("day").toDate();
          if (view === "day") {
            refDate = moment(currentDate)
              .clone()
              .subtract(TIMELINE_BUFFER_UNITS.DAY + 1, "days")
              .toDate();
          } else if (view === "week") {
            const targetStartDate = moment(currentDate)
              .clone()
              .subtract(TIMELINE_BUFFER_UNITS.WEEK, "days");
            refDate = targetStartDate.toDate();
          } else if (view === "3day") {
            refDate = moment(currentDate)
              .clone()
              .subtract(TIMELINE_BUFFER_UNITS["3DAY"], "days")
              .toDate();
          } else if (view === "month") {
            refDate = moment(currentDate)
              .clone()
              .subtract(TIMELINE_BUFFER_UNITS.MONTH, "months")
              .startOf("month")
              .toDate();
          } else {
            refDate = moment(currentDate)
              .clone()
              .subtract(TIMELINE_BUFFER_UNITS.DAY, "days")
              .toDate();
          }
          return refDate;
        })()}
        onSelectedDateChange={onSelectedDateChange}
        onPageLoading={onPageLoading}
        onEventCreate={onEventCreate}
        onEventUpdate={onEventUpdate}
        onEventDeleted={onEventDeleted}
        dataTimezone={dataTimeZone}
        displayTimezone={displayTimeZone}
        timezonePlugin={momentTimezone}
        eventOverlap={isRecurringBooking}
      />

      {Boolean(conflicts.length) && (
        <ConflictPanel conflicts={conflicts} />
      )}
      {isTimelineView && (
        <>
          {(isLegendVisible || isTvMode) && <BookingScheduleStatus />}
          {!isTvMode && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                marginLeft: "4px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <OwcIcon name="tip" />
                <span style={{ marginLeft: "4px" }}>
                  <strong>Quick tip:</strong> Click the timeline to define your
                  booking's timeframe
                </span>
              </div>
              <OwcHyperlink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsLegendVisible((prev) => !prev);
                }}
                style={{
                  marginLeft: "4px",
                }}
              >
                {isLegendVisible ? "Hide legend" : "Show legend"}
              </OwcHyperlink>
              <OwcButton
                variant="primary"
                style={{ marginLeft: "auto" }}
                disabled={
                  pendingReservations.length === 0 || !hasBookableEquipment() || hasServerError
                }
                onClick={() => {
                  // Check if user has permission for at least one equipment with pending reservations
                  const unauthorizedEquipment = pendingReservations.filter(
                    (reservation) => {
                      const status = getEquipmentBookingStatus(
                        reservation.resource,
                      );
                      return !status.canBook;
                    },
                  );

                  if (unauthorizedEquipment.length > 0) {
                    const equipmentDetails = unauthorizedEquipment.map(
                      (res) => {
                        const equipment = resourceData.find(
                          (r) => r.id === res.resource,
                        );
                        const status = getEquipmentBookingStatus(res.resource);
                        return {
                          name: equipment?.name || res.resource,
                          reason: status.reason,
                        };
                      },
                    );

                    const equipmentNames = equipmentDetails
                      .map((eq) => eq.name)
                      .join(", ");
                    const reasons = [
                      ...new Set(equipmentDetails.map((eq) => eq.reason)),
                    ].join("; ");

                    toast({
                      message: `Cannot create booking for: ${equipmentNames}. ${reasons}`,
                    });
                    return;
                  }

                  // New flow: build draft booking and go to step 2
                  if (handleCreateBookingFromEquipmentView) {
                    handleCreateBookingFromEquipmentView();
                  }
                }}
                title={
                  hasServerError
                    ? "Unexpected Server Error, please try again later."
                    : pendingReservations.length === 0
                      ? "Create time slots first"
                      : !hasBookableEquipment()
                        ? "No equipment available for booking"
                        : "Create booking for selected time slots"
                }
              >
                Create Booking
              </OwcButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export { generateUserColor };

export default withApollo(CustomEventCalendar);
